import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import BedtimeRoundedIcon from "@mui/icons-material/BedtimeRounded";
import RestaurantRoundedIcon from "@mui/icons-material/RestaurantRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { useNavigate } from "react-router-dom";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import { OwnerRoutineDialog } from "./calendar/OwnerCalendarDialogs";
import { compactActionSx, surfaceSx } from "./center/ownerCenterUi";

const DAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function idOf(row) { return String(row?.id || row?._id || ""); }

function latestFact(entries, type) {
  return [...(entries || [])]
    .filter((row) => row?.structuredData?.dailyFact?.type === type)
    .sort((a, b) => new Date(b.occurredAt || 0) - new Date(a.occurredAt || 0))[0] || null;
}

function LifeCard({ icon, title, description, action, onClick, meta }) {
  return (
    <Box sx={{ ...surfaceSx, p: 1.5, minHeight: 160 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 36, height: 36, borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "action.hover" }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: 15 }}>{title}</Typography>
          {meta ? <Typography sx={{ color: "text.secondary", fontSize: 11.5, fontWeight: 800 }}>{meta}</Typography> : null}
        </Box>
      </Stack>
      <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 12.6, lineHeight: 1.45 }}>{description}</Typography>
      <Button size="small" onClick={onClick} sx={{ ...compactActionSx, mt: 1.2 }}>{action}</Button>
    </Box>
  );
}

export default function OwnerLifePage() {
  const navigate = useNavigate();
  const [routines, setRoutines] = React.useState([]);
  const [entries, setEntries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState("");
  const [error, setError] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [routineResponse, lifeResponse] = await Promise.all([
        ownerApi.listRoutines({ includeInactive: true, limit: 150 }),
        ownerApi.listLifeEntries({ status: "", limit: 120 }),
      ]);
      setRoutines(routineResponse?.routines || []);
      setEntries(lifeResponse?.entries || []);
    } catch (err) {
      setError(formatApiError(err, "No pude cargar tu panel de vida."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const toggleRoutine = async (row) => {
    const id = idOf(row);
    if (!id || saving) return;
    setSaving(id); setError("");
    try {
      await ownerApi.updateRoutine(id, { active: !row.active });
      await load();
    } catch (err) {
      setError(formatApiError(err, "No pude actualizar la rutina."));
    } finally {
      setSaving("");
    }
  };

  const wake = latestFact(entries, "wake_up");
  const sleep = latestFact(entries, "sleep_start");
  const wakeClock = wake?.structuredData?.dailyFact?.clock || "Sin registro";
  const sleepClock = sleep?.structuredData?.dailyFact?.clock || "Sin registro";
  const activeRoutines = routines.filter((row) => row.active);

  return (
    <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", pb: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ sm: "center" }}>
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: ".09em" }}>Vida</Typography>
          <Typography component="h1" sx={{ mt: .15, fontSize: { xs: "1.7rem", sm: "2.15rem" }, fontWeight: 1000, letterSpacing: "-.045em" }}>Rutinas</Typography>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 13.2, maxWidth: 720 }}>Armá manualmente cómo querés vivir el día. Sueño, comida y hábitos son fuentes distintas; la rutina sólo ordena cuándo ocurren.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)} sx={compactActionSx}>Nueva rutina</Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mt: 1.2 }}>{error}</Alert> : null}
      {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={28} /></Box> : (
        <>
          <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,minmax(0,1fr))" }, gap: 1.1 }}>
            <LifeCard icon={<BedtimeRoundedIcon fontSize="small" />} title="Sueño" meta={`Dormir ${sleepClock} · Levantarme ${wakeClock}`} description="Define el marco del día: cuándo te acostás, cuándo te levantás y cuánto descanso real estás acumulando." action="Ver sueño" onClick={() => navigate("/dashboard/owner/habitos", { state: { ownerJarvisUi: { focus: "sleep" } } })} />
            <LifeCard icon={<RestaurantRoundedIcon fontSize="small" />} title="Comida" description="Stock, comidas, consumo y compras. Las rutinas pueden reservar desayuno, almuerzo, merienda o cena sin mezclarlo con tareas de trabajo." action="Ver comida" onClick={() => navigate("/dashboard/owner/comida")} />
            <LifeCard icon={<TrackChangesRoundedIcon fontSize="small" />} title="Hábitos" description="Lectura, orden, ejercicio y otras conductas repetibles. Acá medís constancia; en Rutinas decidís en qué momento del día entran." action="Ver hábitos" onClick={() => navigate("/dashboard/owner/habitos")} />
          </Box>

          <Box sx={{ ...surfaceSx, p: { xs: 1.35, sm: 1.6 }, mt: 1.2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Box>
                <Typography sx={{ fontWeight: 1000, fontSize: 15 }}>Tu secuencia diaria</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>{activeRoutines.length} activas · {routines.length - activeRoutines.length} pausadas</Typography>
              </Box>
              <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => setDialogOpen(true)} sx={compactActionSx}>Agregar</Button>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack divider={<Divider flexItem />}>
              {routines.length ? routines.map((row) => (
                <Stack key={idOf(row)} direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ py: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={.6} alignItems="center" useFlexGap flexWrap="wrap">
                      <Typography sx={{ fontWeight: 950, fontSize: 13.5 }}>{row.name}</Typography>
                      <Chip size="small" label={row.active ? "Activa" : "Pausada"} color={row.active ? "success" : "default"} sx={{ height: 20, fontSize: 10, fontWeight: 900 }} />
                    </Stack>
                    <Stack direction="row" spacing={.7} alignItems="center" sx={{ mt: .35, color: "text.secondary" }}>
                      <ScheduleRoundedIcon sx={{ fontSize: 15 }} />
                      <Typography sx={{ fontSize: 11.8, fontWeight: 800 }}>{row.startTime}–{row.endTime} · {(row.weekdays || []).map((day) => DAY_LABELS[day]).join(" · ")}</Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={.5}>
                    <Button size="small" onClick={() => navigate(`/dashboard/owner/rutinas/${idOf(row)}`)} sx={compactActionSx}>Abrir</Button>
                    <Button size="small" disabled={saving === idOf(row)} startIcon={row.active ? <PauseCircleOutlineRoundedIcon /> : <PlayCircleOutlineRoundedIcon />} onClick={() => toggleRoutine(row)} sx={compactActionSx}>{row.active ? "Pausar" : "Activar"}</Button>
                  </Stack>
                </Stack>
              )) : <Typography sx={{ py: 3, color: "text.secondary", fontSize: 13 }}>Todavía no tenés una secuencia armada. Creá la primera rutina con nombre, días y horario.</Typography>}
            </Stack>
          </Box>
        </>
      )}

      <OwnerRoutineDialog open={dialogOpen} onClose={() => setDialogOpen(false)} selectedDate="" onSaved={load} />
    </Box>
  );
}
